import React from "react";
import { FaShoppingCart, FaCalculator, FaChartLine, FaLightbulb } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const CommerceGeneral = () => {
    const servicesData = [
        {
            icon: <FaCalculator />,
            title: "Comptabilité et Fiscalité Adaptées",
            description: "Tenue comptable, gestion fiscale optimisée et analyse des flux pour une meilleure gestion de votre activité commerciale.",
            color: "#0d6efd"
        },
        {
            icon: <FaShoppingCart />,
            title: "Gestion de la Paie et Obligations Sociales",
            description: "Gestion complète de la paie, déclarations sociales et conseil en droit social pour vos équipes commerciales.",
            color: "#198754"
        },
        {
            icon: <FaChartLine />,
            title: "Pilotage de la Performance",
            description: "Analyse des coûts et marges, gestion des stocks, tableaux de bord personnalisés et prévisions budgétaires.",
            color: "#dc3545"
        },
        {
            icon: <FaLightbulb />,
            title: "Conseil en Gestion et Stratégie",
            description: "Optimisation de la trésorerie, accompagnement juridique et aide à l'investissement pour développer votre commerce.",
            color: "#6610f2"
        }
    ];

    const serviceDetails = {
        comptabilite: [
            {
                title: "Tenue et Révision Comptable",
                description: "Saisie rigoureuse de vos opérations (achats, ventes, trésorerie), établissement de situations intermédiaires, préparation du bilan et du compte de résultat annuels, conformément aux normes en vigueur."
            },
            {
                title: "Gestion Fiscale Optimisée",
                description: "Établissement et télétransmission de toutes vos déclarations fiscales (TVA, Impôt sur les Sociétés, autres taxes spécifiques au commerce), optimisation de votre charge fiscale dans le respect de la législation, assistance en cas de contrôle fiscal."
            },
            {
                title: "Analyse des Flux",
                description: "Suivi précis des encaissements clients et des décaissements fournisseurs pour une meilleure gestion de trésorerie."
            }
        ],
        paie: [
            {
                title: "Établissement des Bulletins de Paie",
                description: "Gestion complète de la paie de vos salariés (vendeurs, magasiniers, administratifs...), en intégrant les spécificités liées aux horaires, primes, etc."
            },
            {
                title: "Déclarations Sociales",
                description: "Prise en charge de l'ensemble de vos déclarations sociales périodiques (CNSS, etc.) et gestion des relations avec les organismes sociaux."
            },
            {
                title: "Conseil en Droit Social",
                description: "Accompagnement sur les contrats de travail, les procédures de rupture, la réglementation du travail applicable à votre secteur."
            }
        ],
        performance: [
            {
                title: "Analyse des Coûts et des Marges",
                description: "Calcul précis du coût de revient de vos produits, analyse fine de vos marges par produit, par catégorie ou par canal de vente pour identifier les leviers de rentabilité."
            },
            {
                title: "Gestion et Valorisation des Stocks",
                description: "Aide à la mise en place de méthodes de suivi et de valorisation des stocks (FIFO, CUMP...), analyse de la rotation des stocks pour éviter les surstocks ou les ruptures."
            },
            {
                title: "Tableaux de Bord Personnalisés",
                description: "Conception et mise à jour de tableaux de bord clairs et pertinents (suivi du chiffre d'affaires, évolution des marges, seuil de rentabilité, suivi de trésorerie...) pour un pilotage efficace de votre activité."
            },
            {
                title: "Budgets Prévisionnels et Suivi Budgétaire",
                description: "Élaboration de prévisions financières (compte de résultat, bilan, trésorerie) et suivi régulier pour anticiper les besoins et ajuster votre stratégie."
            }
        ],
        conseil: [
            {
                title: "Optimisation de la Trésorerie",
                description: "Analyse de votre cycle d'exploitation, mise en place de stratégies pour améliorer votre Besoin en Fonds de Roulement (BFR), recherche de solutions de financement court terme."
            },
            {
                title: "Accompagnement Juridique",
                description: "Conseils sur le choix de la structure juridique, l'évolution de vos statuts, la rédaction de baux commerciaux, etc. (en partenariat si nécessaire avec des avocats spécialisés)."
            },
            {
                title: "Aide à l'Investissement et au Financement",
                description: "Analyse de la rentabilité de vos projets d'investissement (nouveau point de vente, matériel...), aide à la constitution de dossiers de financement."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour le Commerce Général</h1>
                            <p className="fs-5 mb-4">Votre Conseiller et Partenaire Stratégique pour le Succès de Votre activité de Commerce Général</p>
                            <nav aria-label="breadcrumb">
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les défis uniques du commerce général
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Le secteur du commerce général présente des défis uniques : gestion dynamique des stocks, 
                                fluctuations des ventes, optimisation des marges, pression concurrentielle et complexité fiscale 
                                (notamment la TVA). Chez CINEF-EC, nous comprenons parfaitement les enjeux spécifiques auxquels 
                                votre entreprise est confrontée au quotidien. Forts de notre expertise sectorielle, nous vous 
                                proposons un accompagnement comptable, fiscal, social et de gestion sur mesure, conçu pour sécuriser 
                                votre activité, optimiser votre performance et soutenir votre croissance.
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Pourquoi Choisir CINEF-EC pour Votre Commerce ?</h3>
                            <div className="d-flex flex-wrap mt-4">
                                <div className="d-flex align-items-center me-4 mb-3">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <i className="fas fa-check text-white fs-4"></i>
                                    </div>
                                    <span className="ms-2 fw-bold">Expertise Sectorielle Prouvée</span>
                                </div>
                                <div className="d-flex align-items-center me-4 mb-3">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <i className="fas fa-user-tie text-white fs-4"></i>
                                    </div>
                                    <span className="ms-2 fw-bold">Approche Personnalisée</span>
                                </div>
                                <div className="d-flex align-items-center me-4 mb-3">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <i className="fas fa-lightbulb text-white fs-4"></i>
                                    </div>
                                    <span className="ms-2 fw-bold">Proactivité et Conseil</span>
                                </div>
                                <div className="d-flex align-items-center me-4 mb-3">
                                    <div className="rounded-circle bg-info d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <i className="fas fa-shield-alt text-white fs-4"></i>
                                    </div>
                                    <span className="ms-2 fw-bold">Rigueur et Fiabilité</span>
                                </div>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <i className="fas fa-laptop text-white fs-4"></i>
                                    </div>
                                    <span className="ms-2 fw-bold">Outils Modernes</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold custom-title text-center mb-4">Nos Services Dédiés au Secteur du Commerce Général</h2>
                            <p className="text-muted fs-5 mb-5">Nous avons développé une gamme de services spécifiquement adaptés aux besoins des entreprises de négoce, de distribution et de vente au détail</p>
                        </div>
                    </div>
                    
                    <div className="row">
                        {servicesData.map((service, index) => (
                            <div className="col-md-6 col-lg-3 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>
                                        {service.icon}
                                    </ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Comptabilité et Fiscalité Adaptées à Votre Activité</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.comptabilite.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Gestion de la Paie et Obligations Sociales</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.paie.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`paie-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Pilotage de la Performance et Aide à la Décision</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.performance.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`performance-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">4. Conseil en Gestion et Stratégie</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.conseil.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`conseil-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Passez à l'Étape Supérieure !</h3>
                                <p className="text-white fs-5 mb-4">
                                    Libérez-vous des contraintes administratives et concentrez-vous sur le cœur de votre métier : le commerce. 
                                    Confiez la gestion comptable et financière de votre entreprise à des experts dédiés.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander une consultation gratuite</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/commerce-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default CommerceGeneral;